import {
  InfiniteData,
  useInfiniteQuery,
  UseInfiniteQueryOptions,
  useMutation,
  UseMutationOptions,
  useQuery,
  useQueryClient,
  UseQueryOptions,
} from '@tanstack/react-query';
import {
  api,
  ContactDto,
  ContactsListDto,
  CreateContactDto,
  InferSearchParamsQueryType,
  QueryKey,
  UpdateContactDto,
  VendorContactsListDto,
} from '@sib/api';

interface UseInfiniteClientContactsProps {
  organizationId: string;
  payload: InferSearchParamsQueryType<
    typeof api.organization.organizationContactsControllerGetOrganizationContacts,
    1,
    false
  >;
  options?: Partial<
    UseInfiniteQueryOptions<
      ContactsListDto,
      Error,
      InfiniteData<ContactsListDto>
    >
  >;
}

export const useInfiniteClientContacts = ({
  organizationId,
  payload,
  options,
}: UseInfiniteClientContactsProps) =>
  useInfiniteQuery({
    ...options,
    queryKey: [
      QueryKey.Resource.ClientContacts,
      QueryKey.Operation.List,
      organizationId,
      payload,
      QueryKey.Operation.Infinite,
    ],
    queryFn: ({ pageParam }) =>
      api.organization.organizationContactsControllerGetOrganizationContacts(
        organizationId,
        {
          ...payload,
          page: pageParam as number,
        }
      ),
    getNextPageParam: lastPage =>
      lastPage.hasNextPage ? lastPage.page + 1 : undefined,
    initialPageParam: payload.page,
  });

interface UseInfiniteVendorContactsProps {
  organizationId: string;
  payload: InferSearchParamsQueryType<
    typeof api.organization.organizationContactsControllerGetAllVendorContactsOfOrganization,
    1,
    false
  >;
  options?: Partial<
    UseInfiniteQueryOptions<
      VendorContactsListDto,
      Error,
      InfiniteData<VendorContactsListDto>
    >
  >;
}

export const useInfiniteVendorContacts = ({
  organizationId,
  payload,
  options,
}: UseInfiniteVendorContactsProps) =>
  useInfiniteQuery({
    ...options,
    queryKey: [
      QueryKey.Resource.VendorContacts,
      QueryKey.Operation.List,
      organizationId,
      payload,
      QueryKey.Operation.Infinite,
    ],
    queryFn: ({ pageParam }) =>
      api.organization.organizationContactsControllerGetAllVendorContactsOfOrganization(
        organizationId,
        {
          ...payload,
          page: pageParam as number,
        }
      ),
    getNextPageParam: lastPage =>
      lastPage.hasNextPage ? lastPage.page + 1 : undefined,
    initialPageParam: payload.page,
  });

interface UseGetMainPointOfContactProps {
  organizationId: string;
  query?: InferSearchParamsQueryType<
    typeof api.internalPortal.organizationsControllerGetMainPointOfContact,
    1,
    false
  >;
  options?: Partial<
    UseQueryOptions<ContactDto | null, Error, ContactDto | null>
  >;
}

export const useGetMainPointOfContact = ({
  organizationId,
  query,
  options,
}: UseGetMainPointOfContactProps) =>
  useQuery({
    ...options,
    queryKey: [
      QueryKey.Resource.OrganizationMainPointOfContact,
      organizationId,
      query,
    ],
    queryFn: () =>
      api.internalPortal.organizationsControllerGetMainPointOfContact(
        organizationId,
        query
      ),
  });

export const useGetOneContact = (
  contactId: string,
  options?: Partial<UseQueryOptions<ContactDto>>
) =>
  useQuery({
    ...options,
    enabled: !!contactId && (options?.enabled ?? true),
    queryKey: [
      QueryKey.Resource.VendorContacts,
      QueryKey.Operation.One,
      contactId,
    ],
    queryFn: async () => api.contacts.contactsControllerGetContact(contactId),
  });

export const useVendorContactCreate = (
  options?: Partial<UseMutationOptions<ContactDto, Error, CreateContactDto>>
) => {
  const queryClient = useQueryClient();

  return useMutation({
    ...options,
    mutationFn: (data: CreateContactDto) =>
      api.contacts.contactsControllerCreateContact(data),
    onSuccess: (data, variables, context) => {
      queryClient.invalidateQueries({
        queryKey: [QueryKey.Resource.ClientContacts],
      });
      queryClient.invalidateQueries({
        queryKey: [QueryKey.Resource.VendorContacts],
      });
      options?.onSuccess?.(data, variables, context);
    },
  });
};

export const useVendorContactUpdate = (
  options?: Partial<
    UseMutationOptions<
      ContactDto,
      Error,
      { contactId: string; data: UpdateContactDto }
    >
  >
) => {
  const queryClient = useQueryClient();

  return useMutation({
    ...options,
    mutationFn: ({ contactId, data }) =>
      api.contacts.contactsControllerUpdateContact(contactId, data),
    onSuccess: (data, variables, context) => {
      queryClient.invalidateQueries({
        queryKey: [
          QueryKey.Resource.VendorContacts,
          QueryKey.Operation.One,
          variables.contactId,
        ],
      });
      queryClient.invalidateQueries({
        queryKey: [QueryKey.Resource.ClientContacts],
      });
      queryClient.invalidateQueries({
        queryKey: [QueryKey.Resource.VendorContacts],
      });
      options?.onSuccess?.(data, variables, context);
    },
  });
};

export const useVendorContactDelete = (
  options?: Partial<UseMutationOptions<ContactDto, Error, string>>
) => {
  const queryClient = useQueryClient();

  return useMutation({
    ...options,
    mutationFn: (contactId: string) =>
      api.contacts.contactsControllerDeleteContact(contactId),
    onSuccess: (data, variables, context) => {
      queryClient.invalidateQueries({
        queryKey: [
          QueryKey.Resource.VendorContacts,
          QueryKey.Operation.One,
          variables,
        ],
      });
      queryClient.invalidateQueries({
        queryKey: [QueryKey.Resource.ClientContacts],
      });
      queryClient.invalidateQueries({
        queryKey: [QueryKey.Resource.VendorContacts],
      });
      options?.onSuccess?.(data, variables, context);
    },
  });
};
